import React from 'react';
import { Lesson } from '../data/mockLessons';
import { Globe, Briefcase, Coffee, Heart, ShoppingBag, Home, Monitor, MapPin, Smile, Book, Play } from 'lucide-react';
import { motion } from 'framer-motion';

import { getUITranslation } from '../utils/translation';

interface LessonCardProps {
  lesson: Lesson;
  isSelected: boolean;
  targetLanguage?: string;
  onClick: () => void;
}

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Travel': return <Globe className="w-3.5 h-3.5" />;
    case 'Work': return <Briefcase className="w-3.5 h-3.5" />;
    case 'Food': return <Coffee className="w-3.5 h-3.5" />;
    case 'Health': return <Heart className="w-3.5 h-3.5" />;
    case 'Shopping': return <ShoppingBag className="w-3.5 h-3.5" />;
    case 'Family': return <Home className="w-3.5 h-3.5" />;
    case 'Technology': return <Monitor className="w-3.5 h-3.5" />;
    case 'Greetings': return <Smile className="w-3.5 h-3.5" />;
    case 'Culture': return <Book className="w-3.5 h-3.5" />;
    default: return <MapPin className="w-3.5 h-3.5" />;
  }
};

export const LessonCard: React.FC<LessonCardProps> = ({ lesson, isSelected, onClick, targetLanguage = 'Spanish' }) => {
  const lessonNumber = lesson.id.split('-')[1];
  const translatedCategory = getUITranslation(lesson.category, targetLanguage);
  const translatedLevel = getUITranslation(lesson.level.toLowerCase(), targetLanguage);
  const translatedPractice = getUITranslation('practice', targetLanguage);
  
  const translatedTitle = `${translatedCategory} ${translatedPractice} ${lessonNumber}`;
  const translatedDescription = `${getUITranslation('dailyPracticeFor', targetLanguage)} ${translatedLevel} ${getUITranslation('level', targetLanguage)}: ${translatedCategory}`;

  return (
    <motion.div
      whileHover={{ y: -2, transition: { duration: 0.2 } }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`group relative p-4 rounded-xl border transition-all duration-300 cursor-pointer overflow-hidden ${
        isSelected
          ? 'border-blue-500 bg-blue-600 shadow-lg shadow-blue-900/20'
          : 'border-slate-100 bg-white hover:border-blue-200 hover:shadow-md hover:shadow-blue-900/5'
      }`}
    >
      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-0 right-0 p-2">
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <Play className="w-3 h-3 text-white fill-white" />
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider">
          <span className={`p-1.5 rounded-md transition-colors ${
            isSelected 
              ? 'bg-white/10 text-blue-100' 
              : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100 group-hover:text-blue-700'
          }`}>
            {getCategoryIcon(lesson.category)}
          </span>
          <span className={isSelected ? 'text-blue-100' : 'text-slate-400'}>
            {translatedCategory}
          </span>
        </div>
      </div>
      
      <h3 className={`font-display font-bold text-base mb-1.5 transition-colors ${
        isSelected ? 'text-white' : 'text-slate-900 group-hover:text-blue-700'
      }`}>
        {translatedTitle}
      </h3>
      
      <p className={`text-xs line-clamp-2 leading-relaxed ${
        isSelected ? 'text-blue-100' : 'text-slate-500'
      }`}>
        {translatedDescription}
      </p>

      <div className="mt-3 flex items-center gap-2">
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border ${
            lesson.level === 'Beginner'
              ? isSelected ? 'bg-emerald-500/20 text-emerald-100 border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : lesson.level === 'Intermediate'
              ? isSelected ? 'bg-amber-500/20 text-amber-100 border-amber-500/20' : 'bg-amber-50 text-amber-700 border-amber-100'
              : isSelected ? 'bg-rose-500/20 text-rose-100 border-rose-500/20' : 'bg-rose-50 text-rose-700 border-rose-100'
          }`}
        >
          {translatedLevel}
        </span>
      </div>
    </motion.div>
  );
};
